import { useState, useEffect } from 'react';

/**
 * DOS-415 Phase 4b — the frontend's single source of model truth.
 *
 * Before this, four components each carried their own hardcoded model array
 * (`ModelSelector`, `SettingsModal`, `SearchFilters`, `ChatPage`) and a fifth
 * hardcoded the context-window sizes (`ContextWindowIndicator`). They drifted:
 * three showed capability labels, one showed real model names. Meanwhile
 * `GET /api/claude/models` existed, was served by `listModels()`, and nothing
 * called it.
 *
 * This hook calls it once per page load and shares the result.
 *
 * Labels
 * ------
 * The API returns two names per model and they are not interchangeable:
 *
 *   name      — the real model name, e.g. "Claude Sonnet 4.5". Administrators.
 *   tierLabel — the capability abstraction, e.g. "High Accuracy Model". Users.
 *
 * Everything user-facing reads `tierLabel`. The server guarantees it is always
 * populated, falling back to `name` when an admin has not set one, so callers
 * never have to defend against undefined.
 *
 * Caching
 * -------
 * Module-level, with in-flight de-duplication, so five components mounting at
 * once produce one request rather than five. Deliberately not a React context:
 * this is read-only reference data that changes a few times a year, and a
 * provider would mean wiring every consumer's tree for no benefit.
 *
 * Failure
 * -------
 * On a failed fetch the hook reports `error` and an empty list rather than
 * substituting a hardcoded catalog — reintroducing one here is exactly what
 * this phase removes. Consumers render the current model id and disable
 * switching, which is honest about not knowing rather than confidently wrong.
 */

/**
 * The one bootstrap literal left in the frontend.
 *
 * Something has to seed `useState` before the first fetch resolves, and every
 * component that needs it now imports this instead of writing its own copy —
 * there were eight. It is a starting value, never a source of truth: the
 * catalog's `default` replaces it as soon as the response lands.
 */
export const BOOTSTRAP_MODEL_ID = 'claude-sonnet-4-5-20250929';

/**
 * Which model a conversation continues on. A conversation keeps its stored
 * model while the catalog still offers it; otherwise (an admin disabled it)
 * it moves to the user's preference if that is offered, else the catalog
 * default. Returns null while there is nothing to decide (no stored model, or
 * the catalog hasn't loaded).
 *
 * @param {string|null} storedModel
 * @param {string[]} offeredIds - ids the catalog currently offers
 * @param {string|null} preference - the user's saved default, if any
 * @param {string} catalogDefault
 * @returns {{model: string, switched: boolean}|null}
 */
export function conversationModel(storedModel, offeredIds, preference, catalogDefault) {
  if (!storedModel || offeredIds.length === 0) return null;
  if (offeredIds.includes(storedModel)) return { model: storedModel, switched: false };
  const model = preference && offeredIds.includes(preference) ? preference : catalogDefault;
  return { model, switched: true };
}

let _cache = null;      // last successful payload
let _inflight = null;   // shared promise while a fetch is open

/** Reset module state. Tests only. */
export function __resetModelCatalogCache() {
  _cache = null;
  _inflight = null;
}

async function fetchCatalog() {
  const res = await fetch('/api/claude/models', { credentials: 'include' });
  if (!res.ok) throw new Error(`Model catalog request failed (${res.status})`);
  const data = await res.json();
  return {
    models: Array.isArray(data.models) ? data.models : [],
    defaultModel: data.default || BOOTSTRAP_MODEL_ID,
    demoMode: !!data.demoMode,
  };
}

/**
 * @returns {{
 *   models: Array<{id: string, name: string, tierLabel: string, contextWindow: number, maxOutput: number}>,
 *   defaultModel: string,
 *   demoMode: boolean,
 *   loading: boolean,
 *   error: Error|null,
 *   getTierLabel: (modelId: string) => string,
 *   getContextWindow: (modelId: string) => number|null,
 *   thinksAlways: (modelId: string) => boolean,
 * }}
 */
export function useModelCatalog() {
  const [state, setState] = useState(() =>
    _cache
      ? { ..._cache, loading: false, error: null }
      : { models: [], defaultModel: BOOTSTRAP_MODEL_ID, demoMode: false, loading: true, error: null },
  );

  useEffect(() => {
    let cancelled = false;

    if (_cache) {
      setState({ ..._cache, loading: false, error: null });
      return undefined;
    }

    _inflight = _inflight || fetchCatalog();
    _inflight
      .then((data) => {
        _cache = data;
        _inflight = null;
        if (!cancelled) setState({ ...data, loading: false, error: null });
      })
      .catch((err) => {
        _inflight = null;
        if (!cancelled) {
          setState((s) => ({ ...s, loading: false, error: err }));
        }
      });

    return () => { cancelled = true; };
  }, []);

  /**
   * The user-facing label for a model id. Falls back to the id itself for a
   * model the catalog no longer lists — a conversation started on a model an
   * admin has since removed still has to render something, and showing the id
   * is more honest than inventing a name.
   */
  const getTierLabel = (modelId) => {
    const m = state.models.find((x) => x.id === modelId);
    return m ? (m.tierLabel || m.name) : modelId;
  };

  const getContextWindow = (modelId) => {
    const m = state.models.find((x) => x.id === modelId);
    return m ? m.contextWindow : null;
  };

  /**
   * True for a model that cannot turn thinking off (catalog thinking_off =
   * 'never', e.g. Opus 5). The server sends thinking on every request to such
   * a model whatever the user's toggle says, so the UI must show it too.
   */
  const thinksAlways = (modelId) => {
    const m = state.models.find((x) => x.id === modelId);
    return m?.thinkingOff === 'never';
  };

  return { ...state, getTierLabel, getContextWindow, thinksAlways };
}

export default useModelCatalog;
